import React, { useState, useEffect, createContext, useContext, useMemo, useCallback } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { 
  Cube, ShoppingCart, List, X, CaretRight, 
  Upload, Package, Truck, Clock, CheckCircle,
  Plus, Minus, Trash, ArrowRight, Gear
} from "@phosphor-icons/react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Cart Context
const CartContext = createContext();

export const useCart = () => useContext(CartContext);

const CartProvider = ({ children }) => {
  const [cart, setCart] = useState([]);

  const addToCart = useCallback((product) => {
    setCart(prev => {
      const existing = prev.find(item => item.product_id === product.id);
      if (existing) {
        return prev.map(item => 
          item.product_id === product.id 
            ? { ...item, quantity: item.quantity + 1 }
            : item
        );
      }
      return [...prev, {
        product_id: product.id,
        name: product.name,
        price: product.price,
        quantity: 1,
        image_url: product.image_url
      }];
    });
    toast.success(`${product.name} added to cart`);
  }, []);

  const removeFromCart = useCallback((productId) => {
    setCart(prev => prev.filter(item => item.product_id !== productId));
  }, []);

  const updateQuantity = useCallback((productId, quantity) => {
    if (quantity <= 0) {
      setCart(prev => prev.filter(item => item.product_id !== productId));
      return;
    }
    setCart(prev => prev.map(item => 
      item.product_id === productId ? { ...item, quantity } : item
    ));
  }, []);

  const clearCart = useCallback(() => setCart([]), []);

  const cartTotal = useMemo(() => 
    cart.reduce((sum, item) => sum + (item.price * item.quantity), 0), 
    [cart]
  );
  
  const cartCount = useMemo(() => 
    cart.reduce((sum, item) => sum + item.quantity, 0), 
    [cart]
  );

  const contextValue = useMemo(() => ({ 
    cart, addToCart, removeFromCart, updateQuantity, clearCart, cartTotal, cartCount 
  }), [cart, addToCart, removeFromCart, updateQuantity, clearCart, cartTotal, cartCount]);

  return (
    <CartContext.Provider value={contextValue}>
      {children}
    </CartContext.Provider>
  );
};

// Header Component
const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { cartCount } = useCart();

  const navLinks = useMemo(() => [
    { name: "Products", href: "/#products" },
    { name: "Custom Order", href: "/#custom-order" },
    { name: "Track Order", href: "/#tracker" },
  ], []);

  return (
    <>
      <header className="sticky top-0 z-50 bg-[#050505]/80 glass border-b border-white/10" data-testid="main-header">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="brand-logo">
            <Cube size={32} weight="fill" className="text-[#00AE42]" />
            <span className="font-black text-xl tracking-tight">FUSION FLUX 3D</span>
          </Link>

          <nav className="hidden md:flex items-center gap-8">
            {navLinks.map(link => (
              <a 
                key={link.name}
                href={link.href}
                className="text-sm text-white/70 hover:text-[#00AE42] transition-colors uppercase tracking-wider"
                data-testid={`nav-${link.name.toLowerCase().replace(' ', '-')}`}
              >
                {link.name}
              </a>
            ))}
          </nav>

          <div className="flex items-center gap-4">
            <Link to="/cart" className="relative p-2" data-testid="cart-button">
              <ShoppingCart size={24} className="text-white" />
              {cartCount > 0 && (
                <span className="cart-badge" data-testid="cart-count">{cartCount}</span>
              )}
            </Link>
            <button 
              className="md:hidden p-2"
              onClick={() => setMobileMenuOpen(true)}
              data-testid="mobile-menu-toggle"
            >
              <List size={24} className="text-white" />
            </button>
          </div>
        </div>
      </header>

      {mobileMenuOpen && (
        <div className="mobile-menu" data-testid="mobile-menu">
          <button 
            className="absolute top-6 right-6"
            onClick={() => setMobileMenuOpen(false)}
            data-testid="close-mobile-menu"
          >
            <X size={32} className="text-white" />
          </button>
          {navLinks.map(link => (
            <a 
              key={link.name}
              href={link.href}
              onClick={() => setMobileMenuOpen(false)}
              className="text-2xl text-white hover:text-[#00AE42] transition-colors"
            >
              {link.name}
            </a>
          ))}
        </div>
      )}
    </>
  );
};

// Footer Component
const Footer = () => {
  const [socialLinks, setSocialLinks] = useState([]);

  useEffect(() => {
    const fetchSocialLinks = async () => {
      try {
        const res = await axios.get(`${API}/social-links`);
        setSocialLinks(res.data);
      } catch {
        // Silent fail for social links
      }
    };
    fetchSocialLinks();
  }, []);

  return (
    <footer className="bg-[#111111] border-t border-white/10 py-12" data-testid="footer">
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex flex-col md:flex-row justify-between items-center gap-6">
          <div className="flex items-center gap-3">
            <Cube size={24} weight="fill" className="text-[#00AE42]" />
            <span className="font-bold">FUSION FLUX 3D</span>
          </div>
          {socialLinks.length > 0 && (
            <div className="flex gap-4">
              {socialLinks.map(link => (
                <a 
                  key={link.id}
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-white/60 hover:text-[#00AE42] transition-colors"
                >
                  {link.platform}
                </a>
              ))}
            </div>
          )}
          <p className="text-sm text-white/40">© 2024 Fusion Flux 3D. All rights reserved.</p>
        </div>
      </div>
    </footer>
  );
};

// Hero Section
const HeroSection = () => (
  <section className="hero-bg min-h-[60vh] flex items-center border-b border-white/10" data-testid="hero-section">
    <div className="max-w-7xl mx-auto px-6 py-20">
      <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter mb-6 max-w-2xl">
        PRECISION 3D PRINTING<br />
        <span className="text-[#00AE42]">FOR YOUR VISION</span>
      </h1>
      <p className="text-lg text-white/60 mb-8 max-w-lg">
        From custom designs to ready-made products, we bring your ideas to life with premium quality prints.
      </p>
      <a 
        href="#products" 
        className="btn-solid inline-flex items-center gap-2 px-8 py-4 font-bold text-sm uppercase tracking-wider"
        data-testid="hero-cta"
      >
        Browse Products <CaretRight size={20} weight="bold" />
      </a>
    </div>
  </section>
);

// Product Gallery
const ProductGallery = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const { addToCart } = useCart();

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const res = await axios.get(`${API}/products`);
        setProducts(res.data);
      } catch {
        // Silent fail
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, []);

  if (loading) {
    return (
      <section id="products" className="py-20 px-6" data-testid="products-section">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-bold mb-12">OUR PRODUCTS</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[1,2,3].map(i => (
              <div key={i} className="bg-[#111111] border border-white/10 animate-pulse">
                <div className="aspect-square bg-[#1A1A1A]"></div>
                <div className="p-6 space-y-4">
                  <div className="h-6 bg-[#1A1A1A] rounded"></div>
                  <div className="h-4 bg-[#1A1A1A] rounded w-2/3"></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="products" className="py-20 px-6" data-testid="products-section">
      <div className="max-w-7xl mx-auto">
        <h2 className="text-2xl sm:text-3xl font-bold mb-12">OUR PRODUCTS</h2>
        {products.length === 0 ? (
          <div className="text-center py-20 text-white/60">
            <Package size={64} className="mx-auto mb-4 opacity-50" />
            <p>No products available yet. Check back soon!</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {products.map((product, idx) => (
              <div 
                key={product.id} 
                className="product-card bg-[#111111] border border-white/10 overflow-hidden flex flex-col"
                style={{ animationDelay: `${idx * 100}ms` }}
                data-testid={`product-card-${product.id}`}
              >
                <div className="aspect-square bg-[#1A1A1A] overflow-hidden p-4 flex items-center justify-center">
                  <img 
                    src={product.image_url} 
                    alt={product.name}
                    className="w-full h-full object-contain"
                  />
                </div>
                <div className="p-6 flex flex-col flex-grow">
                  <h3 className="text-lg font-bold mb-2">{product.name}</h3>
                  <p className="text-sm text-white/60 mb-4 flex-grow">{product.description}</p>
                  <div className="flex items-center justify-between">
                    <span className="text-xl font-bold text-[#00AE42]">£{product.price.toFixed(2)}</span>
                    <button 
                      onClick={() => addToCart(product)}
                      className="btn-primary px-6 py-3 text-sm font-bold uppercase tracking-wider"
                      data-testid={`buy-now-${product.id}`}
                    >
                      Buy Now
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

// Custom Order Form
const CustomOrderForm = () => {
  const [formData, setFormData] = useState({
    customer_name: '',
    customer_email: '',
    material: 'PLA',
    color: '',
    notes: ''
  });
  const [file, setFile] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const materials = useMemo(() => ['PLA', 'PETG', 'TPU'], []);
  const colors = useMemo(() => ['White', 'Black', 'Red', 'Blue', 'Green', 'Yellow', 'Orange', 'Purple', 'Custom'], []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile && (droppedFile.name.endsWith('.stl') || droppedFile.name.endsWith('.obj'))) {
      setFile(droppedFile);
    } else {
      toast.error('Please upload a .STL or .OBJ file');
    }
  }, []);

  const handleFileSelect = useCallback((e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile && (selectedFile.name.endsWith('.stl') || selectedFile.name.endsWith('.obj'))) {
      setFile(selectedFile);
    } else {
      toast.error('Please upload a .STL or .OBJ file');
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!file) {
      toast.error('Please upload a 3D model file');
      return;
    }
    if (!formData.color) {
      toast.error('Please select a color');
      return;
    }

    setSubmitting(true);
    const data = new FormData();
    data.append('file', file);
    Object.entries(formData).forEach(([key, value]) => data.append(key, value));

    try {
      const res = await axios.post(`${API}/custom-orders`, data);
      toast.success(`Quote request submitted! Order #${res.data.order_number}`);
      setFormData({ customer_name: '', customer_email: '', material: 'PLA', color: '', notes: '' });
      setFile(null);
    } catch {
      toast.error('Failed to submit request. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="custom-order" className="py-20 px-6 bg-[#111111]" data-testid="custom-order-section">
      <div className="max-w-7xl mx-auto">
        <h2 className="text-2xl sm:text-3xl font-bold mb-4">CUSTOM ORDER</h2>
        <p className="text-white/60 mb-12">Upload your 3D model and get a quote</p>

        <form onSubmit={handleSubmit} className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          <div 
            className={`upload-zone ${dragOver ? 'drag-over' : ''} min-h-[300px] flex flex-col items-center justify-center cursor-pointer`}
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
            onClick={() => document.getElementById('file-input').click()}
            data-testid="file-upload-zone"
          >
            <input 
              type="file" 
              id="file-input" 
              className="hidden" 
              accept=".stl,.obj"
              onChange={handleFileSelect}
            />
            <Upload size={48} className="text-white/40 mb-4" />
            {file ? (
              <div className="text-center">
                <p className="text-[#00AE42] font-bold">{file.name}</p>
                <p className="text-sm text-white/40 mt-2">Click or drag to replace</p>
              </div>
            ) : (
              <>
                <p className="text-white/60 mb-2">Drag & drop your 3D file here</p>
                <p className="text-sm text-white/40">.STL or .OBJ files accepted</p>
              </>
            )}
          </div>

          <div className="space-y-6">
            <div>
              <label className="block text-sm text-white/60 mb-2 uppercase tracking-wider">Name</label>
              <input 
                type="text"
                required
                value={formData.customer_name}
                onChange={(e) => setFormData({...formData, customer_name: e.target.value})}
                className="input-dark"
                placeholder="Your name"
                data-testid="custom-order-name"
              />
            </div>

            <div>
              <label className="block text-sm text-white/60 mb-2 uppercase tracking-wider">Email</label>
              <input 
                type="email"
                required
                value={formData.customer_email}
                onChange={(e) => setFormData({...formData, customer_email: e.target.value})}
                className="input-dark"
                placeholder="your@email.com"
                data-testid="custom-order-email"
              />
            </div>

            <div>
              <label className="block text-sm text-white/60 mb-2 uppercase tracking-wider">Material</label>
              <div className="flex gap-4 flex-wrap">
                {materials.map(mat => (
                  <button 
                    key={mat}
                    type="button"
                    onClick={() => setFormData({...formData, material: mat})}
                    className={`px-6 py-3 border transition-all ${
                      formData.material === mat 
                        ? 'border-[#00AE42] bg-[#00AE42]/10 text-[#00AE42]' 
                        : 'border-white/20 text-white/60 hover:border-white/40'
                    }`}
                    data-testid={`material-${mat.toLowerCase()}`}
                  >
                    {mat}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm text-white/60 mb-2 uppercase tracking-wider">Color</label>
              <div className="flex gap-3 flex-wrap">
                {colors.map(color => (
                  <button 
                    key={color}
                    type="button"
                    onClick={() => setFormData({...formData, color})}
                    className={`px-4 py-2 border text-sm transition-all ${
                      formData.color === color 
                        ? 'border-[#00AE42] bg-[#00AE42]/10 text-[#00AE42]' 
                        : 'border-white/20 text-white/60 hover:border-white/40'
                    }`}
                    data-testid={`color-${color.toLowerCase()}`}
                  >
                    {color}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm text-white/60 mb-2 uppercase tracking-wider">Additional Notes</label>
              <textarea 
                value={formData.notes}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
                className="input-dark min-h-[100px] resize-none"
                placeholder="Any special requirements..."
                data-testid="custom-order-notes"
              />
            </div>

            <button 
              type="submit"
              disabled={submitting}
              className="btn-solid w-full py-4 font-bold text-sm uppercase tracking-wider disabled:opacity-50"
              data-testid="submit-custom-order"
            >
              {submitting ? 'Submitting...' : 'Request Quote'}
            </button>
          </div>
        </form>
      </div>
    </section>
  );
};

// Order Status Tracker
const OrderTracker = () => {
  const [orderNumber, setOrderNumber] = useState('');
  const [orderStatus, setOrderStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const statuses = useMemo(() => ['In Queue', 'Printing', 'Shipped'], []);

  const handleTrack = async (e) => {
    e.preventDefault();
    if (!orderNumber.trim()) return;

    setLoading(true);
    setError('');
    setOrderStatus(null);

    try {
      const res = await axios.get(`${API}/orders/${orderNumber.trim()}/status`);
      setOrderStatus(res.data);
    } catch {
      setError('Order not found. Please check your order number.');
    } finally {
      setLoading(false);
    }
  };

  const getStatusIndex = useCallback((status) => {
    const idx = statuses.findIndex(s => s === status);
    return idx >= 0 ? idx : -1;
  }, [statuses]);

  const currentStatusIndex = orderStatus ? getStatusIndex(orderStatus.status) : -1;

  return (
    <section id="tracker" className="py-20 px-6" data-testid="tracker-section">
      <div className="max-w-3xl mx-auto">
        <h2 className="text-2xl sm:text-3xl font-bold mb-4">TRACK YOUR ORDER</h2>
        <p className="text-white/60 mb-8">Enter your order number to check the status</p>

        <div className="bg-[#111111] border-l-4 border-[#00AE42] p-8" data-testid="tracker-box">
          <form onSubmit={handleTrack} className="flex gap-4 flex-col sm:flex-row">
            <input 
              type="text"
              value={orderNumber}
              onChange={(e) => setOrderNumber(e.target.value.toUpperCase())}
              placeholder="FF-XXXXXXXX"
              className="input-dark mono text-xl tracking-widest uppercase flex-grow"
              data-testid="order-number-input"
            />
            <button 
              type="submit"
              disabled={loading}
              className="btn-solid px-8 py-4 font-bold text-sm uppercase tracking-wider whitespace-nowrap"
              data-testid="track-order-btn"
            >
              {loading ? 'Tracking...' : 'Track'}
            </button>
          </form>

          {error && (
            <p className="text-[#FF3B30] mt-4 mono text-sm" data-testid="tracker-error">{error}</p>
          )}

          {orderStatus && (
            <div className="mt-8" data-testid="order-status-result">
              <div className="flex items-center gap-2 mb-6">
                <span className="mono text-white/60">Order:</span>
                <span className="mono text-[#00AE42] font-bold">{orderStatus.order_number}</span>
              </div>

              <div className="relative">
                <div className="absolute left-3 top-0 bottom-0 w-px bg-white/10"></div>
                <div className="space-y-8">
                  {statuses.map((status, idx) => {
                    const isActive = idx <= currentStatusIndex;
                    const isCurrent = idx === currentStatusIndex;

                    return (
                      <div key={status} className="flex items-center gap-6 relative">
                        <div className={`timeline-dot ${isActive ? 'active' : 'inactive'} ${isCurrent ? 'glow-pulse' : ''}`}>
                          {idx === 0 && <Clock size={14} className="absolute inset-0 m-auto text-[#050505]" />}
                          {idx === 1 && <Cube size={14} className="absolute inset-0 m-auto text-[#050505]" />}
                          {idx === 2 && <Truck size={14} className="absolute inset-0 m-auto text-[#050505]" />}
                        </div>
                        <span className={`mono uppercase tracking-wider ${isActive ? 'text-white' : 'text-white/40'}`}>
                          {status}
                        </span>
                        {isCurrent && (
                          <span className="status-badge status-printing ml-auto">Current</span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

// Home Page
const Home = () => (
  <>
    <HeroSection />
    <ProductGallery />
    <CustomOrderForm />
    <OrderTracker />
  </>
);

// Cart Page
const CartPage = () => {
  const { cart, removeFromCart, updateQuantity, cartTotal } = useCart();
  const navigate = useNavigate();

  if (cart.length === 0) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center px-6" data-testid="empty-cart">
        <ShoppingCart size={64} className="text-white/20 mb-4" />
        <h2 className="text-2xl font-bold mb-4">Your cart is empty</h2>
        <Link to="/#products" className="btn-primary px-8 py-4 font-bold uppercase tracking-wider">
          Browse Products
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-6 py-12" data-testid="cart-page">
      <h1 className="text-3xl font-bold mb-8">YOUR CART</h1>

      <div className="space-y-4 mb-8">
        {cart.map(item => (
          <div key={item.product_id} className="bg-[#111111] border border-white/10 p-4 flex items-center gap-4" data-testid={`cart-item-${item.product_id}`}>
            <img src={item.image_url} alt={item.name} className="w-20 h-20 object-contain bg-[#1A1A1A]" />
            <div className="flex-grow">
              <h3 className="font-bold">{item.name}</h3>
              <p className="text-[#00AE42]">£{item.price.toFixed(2)}</p>
            </div>
            <div className="flex items-center gap-2">
              <button 
                onClick={() => updateQuantity(item.product_id, item.quantity - 1)}
                className="p-2 border border-white/20 hover:border-[#00AE42] transition-colors"
                data-testid={`decrease-qty-${item.product_id}`}
              >
                <Minus size={16} />
              </button>
              <span className="w-8 text-center mono">{item.quantity}</span>
              <button 
                onClick={() => updateQuantity(item.product_id, item.quantity + 1)}
                className="p-2 border border-white/20 hover:border-[#00AE42] transition-colors"
                data-testid={`increase-qty-${item.product_id}`}
              >
                <Plus size={16} />
              </button>
            </div>
            <button 
              onClick={() => removeFromCart(item.product_id)}
              className="p-2 text-[#FF3B30] hover:bg-[#FF3B30]/10 transition-colors"
              data-testid={`remove-item-${item.product_id}`}
            >
              <Trash size={20} />
            </button>
          </div>
        ))}
      </div>

      <div className="bg-[#111111] border border-white/10 p-6">
        <div className="flex justify-between items-center mb-6">
          <span className="text-lg">Total</span>
          <span className="text-2xl font-bold text-[#00AE42]" data-testid="cart-total">£{cartTotal.toFixed(2)}</span>
        </div>
        <button 
          onClick={() => navigate('/checkout')}
          className="btn-solid w-full py-4 font-bold text-sm uppercase tracking-wider flex items-center justify-center gap-2"
          data-testid="proceed-to-checkout"
        >
          Proceed to Checkout <ArrowRight size={20} />
        </button>
      </div>
    </div>
  );
};

// Checkout Page
const CheckoutPage = () => {
  const { cart, cartTotal, clearCart } = useCart();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [customerInfo, setCustomerInfo] = useState({ 
    first_name: '',
    middle_name: '',
    last_name: '',
    email: '',
    company: '',
    street_address_1: '',
    street_address_2: '',
    country: 'United Kingdom',
    state_province: '',
    city: '',
    postal_code: '',
    phone_number: ''
  });
  const [orderId, setOrderId] = useState('');
  const [orderNumber, setOrderNumber] = useState('');
  const [confirming, setConfirming] = useState(false);

  const countries = useMemo(() => [
    'United Kingdom', 'United States', 'Canada', 'Australia', 'Germany', 
    'France', 'Spain', 'Italy', 'Netherlands', 'Belgium', 'Ireland',
    'Sweden', 'Norway', 'Denmark', 'Finland', 'Austria', 'Switzerland',
    'Poland', 'Portugal', 'Greece', 'Japan', 'South Korea', 'Singapore',
    'New Zealand', 'Other'
  ], []);

  useEffect(() => {
    if (cart.length === 0 && step === 1) {
      navigate('/cart');
    }
  }, [cart.length, step, navigate]);

  const handleCreateOrder = async (e) => {
    e.preventDefault();
    try {
      const fullName = `${customerInfo.first_name} ${customerInfo.middle_name} ${customerInfo.last_name}`.replace(/\s+/g, ' ').trim();
      const res = await axios.post(`${API}/orders`, {
        customer_name: fullName,
        customer_email: customerInfo.email,
        first_name: customerInfo.first_name,
        middle_name: customerInfo.middle_name,
        last_name: customerInfo.last_name,
        company: customerInfo.company,
        street_address_1: customerInfo.street_address_1,
        street_address_2: customerInfo.street_address_2,
        country: customerInfo.country,
        state_province: customerInfo.state_province,
        city: customerInfo.city,
        postal_code: customerInfo.postal_code,
        phone_number: customerInfo.phone_number,
        items: cart,
        total: cartTotal
      });
      setOrderId(res.data.id);
      setOrderNumber(res.data.order_number);
      setStep(2);
    } catch {
      toast.error('Failed to create order. Please try again.');
    }
  };

  const handleConfirmPayment = async () => {
    setConfirming(true);
    try {
      const fullName = `${customerInfo.first_name} ${customerInfo.last_name}`.trim();
      await axios.post(`${API}/orders/${orderId}/confirm-payment`, {
        order_id: orderId,
        customer_name: fullName
      });
      clearCart();
      setStep(3);
    } catch {
      toast.error('Failed to confirm payment. Please try again.');
    } finally {
      setConfirming(false);
    }
  };

  if (step === 3) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center px-6 text-center" data-testid="thank-you-page">
        <CheckCircle size={80} className="text-[#00AE42] mb-6" />
        <h1 className="text-3xl font-bold mb-4">THANK YOU!</h1>
        <p className="text-white/60 mb-2">Your order has been received</p>
        <p className="mono text-[#00AE42] text-xl mb-8" data-testid="final-order-number">{orderNumber}</p>
        <p className="text-white/40 text-sm mb-8">You can track your order status using the order number above</p>
        <Link to="/" className="btn-primary px-8 py-4 font-bold uppercase tracking-wider">
          Back to Shop
        </Link>
      </div>
    );
  }

  if (step === 2) {
    return (
      <div className="max-w-xl mx-auto px-6 py-12" data-testid="payment-page">
        <h1 className="text-3xl font-bold mb-8 text-center">COMPLETE PAYMENT</h1>
        
        <div className="revolut-box" data-testid="revolut-box">
          <p className="text-lg mb-6">To complete your order, please pay via Revolut.</p>
          
          <div className="bg-[#050505] border border-[#00AE42] p-6 mb-6">
            <p className="mono text-sm text-white/60 mb-2">Amount Due</p>
            <p className="text-3xl font-bold text-[#00AE42]" data-testid="amount-due">£{cartTotal.toFixed(2)}</p>
          </div>

          <a 
            href="https://revolut.me/noahnqohl"
            target="_blank"
            rel="noopener noreferrer"
            className="btn-solid w-full py-4 font-bold text-sm uppercase tracking-wider mb-4 block"
            data-testid="pay-here-btn"
          >
            Pay Here
          </a>

          <p className="text-[#FF3B30] text-sm font-bold mb-6">If you have not sent the full amount your order will not be shipped!</p>

          <p className="text-white/60 text-sm mb-6">Add your name so I know it's you</p>

          <button 
            onClick={handleConfirmPayment}
            disabled={confirming}
            className="btn-primary w-full py-4 font-bold text-sm uppercase tracking-wider disabled:opacity-50"
            data-testid="i-have-paid-btn"
          >
            {confirming ? 'Confirming...' : 'I Have Paid'}
          </button>
        </div>

        <p className="text-center text-white/40 text-sm mt-6">
          Order #{orderNumber}
        </p>
      </div>
    );
  }

  return (
    <div className="max-w-xl mx-auto px-6 py-12" data-testid="checkout-form-page">
      <h1 className="text-3xl font-bold mb-8">CHECKOUT</h1>

      <div className="bg-[#111111] border border-white/10 p-6 mb-6">
        <h3 className="text-sm text-white/60 uppercase tracking-wider mb-4">Order Summary</h3>
        {cart.map(item => (
          <div key={item.product_id} className="flex justify-between mb-2">
            <span>{item.name} x{item.quantity}</span>
            <span>£{(item.price * item.quantity).toFixed(2)}</span>
          </div>
        ))}
        <div className="border-t border-white/10 mt-4 pt-4 flex justify-between">
          <span className="font-bold">Total</span>
          <span className="font-bold text-[#00AE42]">£{cartTotal.toFixed(2)}</span>
        </div>
      </div>

      <form onSubmit={handleCreateOrder} className="space-y-5">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm text-white/60 mb-2">First Name <span className="text-[#FF3B30]">*</span></label>
            <input 
              type="text"
              required
              value={customerInfo.first_name}
              onChange={(e) => setCustomerInfo({...customerInfo, first_name: e.target.value})}
              className="input-dark"
              data-testid="checkout-first-name"
            />
          </div>
          <div>
            <label className="block text-sm text-white/60 mb-2">Middle Name/Initial</label>
            <input 
              type="text"
              value={customerInfo.middle_name}
              onChange={(e) => setCustomerInfo({...customerInfo, middle_name: e.target.value})}
              className="input-dark"
              data-testid="checkout-middle-name"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm text-white/60 mb-2">Last Name <span className="text-[#FF3B30]">*</span></label>
          <input 
            type="text"
            required
            value={customerInfo.last_name}
            onChange={(e) => setCustomerInfo({...customerInfo, last_name: e.target.value})}
            className="input-dark"
            data-testid="checkout-last-name"
          />
        </div>

        <div>
          <label className="block text-sm text-white/60 mb-2">Email <span className="text-[#FF3B30]">*</span></label>
          <input 
            type="email"
            required
            value={customerInfo.email}
            onChange={(e) => setCustomerInfo({...customerInfo, email: e.target.value})}
            className="input-dark"
            placeholder="your@email.com"
            data-testid="checkout-email"
          />
        </div>

        <div>
          <label className="block text-sm text-white/60 mb-2">Company</label>
          <input 
            type="text"
            value={customerInfo.company}
            onChange={(e) => setCustomerInfo({...customerInfo, company: e.target.value})}
            className="input-dark"
            data-testid="checkout-company"
          />
        </div>

        <div>
          <label className="block text-sm text-white/60 mb-2 text-center">Street Address <span className="text-[#FF3B30]">*</span></label>
          <input 
            type="text"
            required
            value={customerInfo.street_address_1}
            onChange={(e) => setCustomerInfo({...customerInfo, street_address_1: e.target.value})}
            className="input-dark mb-2"
            placeholder="Address line 1"
            data-testid="checkout-address-1"
          />
          <input 
            type="text"
            value={customerInfo.street_address_2}
            onChange={(e) => setCustomerInfo({...customerInfo, street_address_2: e.target.value})}
            className="input-dark"
            placeholder="Address line 2 (optional)"
            data-testid="checkout-address-2"
          />
        </div>

        <div>
          <label className="block text-sm text-white/60 mb-2">Country <span className="text-[#FF3B30]">*</span></label>
          <select 
            required
            value={customerInfo.country}
            onChange={(e) => setCustomerInfo({...customerInfo, country: e.target.value})}
            className="input-dark"
            data-testid="checkout-country"
          >
            {countries.map(country => (
              <option key={country} value={country}>{country}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm text-white/60 mb-2">State/Province</label>
          <input 
            type="text"
            value={customerInfo.state_province}
            onChange={(e) => setCustomerInfo({...customerInfo, state_province: e.target.value})}
            className="input-dark"
            data-testid="checkout-state"
          />
        </div>

        <div>
          <label className="block text-sm text-white/60 mb-2">City <span className="text-[#FF3B30]">*</span></label>
          <input 
            type="text"
            required
            value={customerInfo.city}
            onChange={(e) => setCustomerInfo({...customerInfo, city: e.target.value})}
            className="input-dark"
            data-testid="checkout-city"
          />
        </div>

        <div>
          <label className="block text-sm text-white/60 mb-2">Zip/Postal Code <span className="text-[#FF3B30]">*</span></label>
          <input 
            type="text"
            required
            value={customerInfo.postal_code}
            onChange={(e) => setCustomerInfo({...customerInfo, postal_code: e.target.value})}
            className="input-dark"
            data-testid="checkout-postal"
          />
        </div>

        <div>
          <label className="block text-sm text-white/60 mb-2">Phone Number <span className="text-[#FF3B30]">*</span></label>
          <input 
            type="tel"
            required
            value={customerInfo.phone_number}
            onChange={(e) => setCustomerInfo({...customerInfo, phone_number: e.target.value})}
            className="input-dark"
            placeholder="+44 7XXX XXXXXX"
            data-testid="checkout-phone"
          />
        </div>

        <button 
          type="submit"
          className="btn-solid w-full py-4 font-bold text-sm uppercase tracking-wider"
          data-testid="continue-to-payment"
        >
          Continue to Payment
        </button>
      </form>
    </div>
  );
};

// Admin Login Component
const AdminLogin = ({ onLogin }) => {
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      const res = await axios.post(`${API}/admin/login`, { password });
      if (res.data.success) {
        onLogin();
      }
    } catch {
      setError('Invalid password');
    }
  };

  return (
    <div className="min-h-[60vh] flex items-center justify-center px-6" data-testid="admin-login">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-3 mb-8 justify-center">
          <Gear size={32} className="text-[#00AE42]" />
          <h1 className="text-2xl font-bold">ADMIN ACCESS</h1>
        </div>
        
        <form onSubmit={handleLogin} className="space-y-6">
          <input 
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Enter password"
            className="input-dark text-center"
            data-testid="admin-password-input"
          />
          {error && <p className="text-[#FF3B30] text-sm text-center">{error}</p>}
          <button 
            type="submit"
            className="btn-solid w-full py-4 font-bold uppercase tracking-wider"
            data-testid="admin-login-btn"
          >
            Login
          </button>
        </form>
      </div>
    </div>
  );
};

// Admin Products Tab
const AdminProductsTab = ({ products, onRefresh }) => {
  const [showForm, setShowForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [productForm, setProductForm] = useState({
    name: '', description: '', price: '', image_url: '', category: 'general', in_stock: true
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = { ...productForm, price: parseFloat(productForm.price) };
      if (editingProduct) {
        await axios.put(`${API}/admin/products/${editingProduct.id}`, data);
        toast.success('Product updated');
      } else {
        await axios.post(`${API}/admin/products`, data);
        toast.success('Product added');
      }
      setShowForm(false);
      setEditingProduct(null);
      setProductForm({ name: '', description: '', price: '', image_url: '', category: 'general', in_stock: true });
      onRefresh();
    } catch {
      toast.error('Failed to save product');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this product?')) return;
    try {
      await axios.delete(`${API}/admin/products/${id}`);
      toast.success('Product deleted');
      onRefresh();
    } catch {
      toast.error('Failed to delete product');
    }
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    setProductForm({ ...product, price: product.price.toString() });
    setShowForm(true);
  };

  return (
    <div>
      <button 
        onClick={() => { setShowForm(true); setEditingProduct(null); setProductForm({ name: '', description: '', price: '', image_url: '', category: 'general', in_stock: true }); }}
        className="btn-solid px-6 py-3 font-bold text-sm uppercase tracking-wider mb-6 flex items-center gap-2"
        data-testid="add-product-btn"
      >
        <Plus size={20} /> Add Product
      </button>

      {showForm && (
        <div className="bg-[#111111] border border-white/10 p-6 mb-6" data-testid="product-form">
          <h3 className="font-bold mb-4">{editingProduct ? 'Edit Product' : 'New Product'}</h3>
          <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <input 
              placeholder="Product Name"
              value={productForm.name}
              onChange={(e) => setProductForm({...productForm, name: e.target.value})}
              className="input-dark"
              required
              data-testid="product-name-input"
            />
            <input 
              placeholder="Price"
              type="number"
              step="0.01"
              value={productForm.price}
              onChange={(e) => setProductForm({...productForm, price: e.target.value})}
              className="input-dark"
              required
              data-testid="product-price-input"
            />
            <input 
              placeholder="Image URL"
              value={productForm.image_url}
              onChange={(e) => setProductForm({...productForm, image_url: e.target.value})}
              className="input-dark md:col-span-2"
              required
              data-testid="product-image-input"
            />
            <textarea 
              placeholder="Description"
              value={productForm.description}
              onChange={(e) => setProductForm({...productForm, description: e.target.value})}
              className="input-dark md:col-span-2 min-h-[80px]"
              required
              data-testid="product-description-input"
            />
            <div className="flex gap-4 md:col-span-2">
              <button type="submit" className="btn-solid px-6 py-3 font-bold text-sm" data-testid="save-product-btn">
                {editingProduct ? 'Update' : 'Add'} Product
              </button>
              <button type="button" onClick={() => setShowForm(false)} className="btn-primary px-6 py-3 text-sm">
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="bg-[#111111] border border-white/10 overflow-x-auto">
        <table className="admin-table">
          <thead>
            <tr>
              <th>Image</th>
              <th>Name</th>
              <th>Price</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {products.map(product => (
              <tr key={product.id} data-testid={`admin-product-${product.id}`}>
                <td><img src={product.image_url} alt="" className="w-12 h-12 object-contain bg-[#1A1A1A]" /></td>
                <td>{product.name}</td>
                <td>£{product.price.toFixed(2)}</td>
                <td><span className={`status-badge ${product.in_stock ? 'status-printing' : 'status-in-queue'}`}>{product.in_stock ? 'In Stock' : 'Out of Stock'}</span></td>
                <td>
                  <div className="flex gap-2">
                    <button onClick={() => handleEdit(product)} className="text-[#00AE42] text-sm hover:underline" data-testid={`edit-product-${product.id}`}>Edit</button>
                    <button onClick={() => handleDelete(product.id)} className="text-[#FF3B30] text-sm hover:underline" data-testid={`delete-product-${product.id}`}>Delete</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {products.length === 0 && <p className="text-center text-white/40 py-8">No products yet</p>}
      </div>
    </div>
  );
};

// Admin Orders Tab
const AdminOrdersTab = ({ orders, onStatusChange }) => {
  const activeOrders = useMemo(() => 
    orders.filter(order => order.status !== 'Shipped' && order.status !== 'Delivered'),
    [orders]
  );

  return (
    <div className="bg-[#111111] border border-white/10 overflow-x-auto">
      <table className="admin-table">
        <thead>
          <tr>
            <th>Order #</th>
            <th>Customer</th>
            <th>Items</th>
            <th>Total</th>
            <th>Status</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {activeOrders.map(order => (
            <tr key={order.id} data-testid={`admin-order-${order.id}`}>
              <td className="mono">{order.order_number}</td>
              <td>
                <div>{order.customer_name}</div>
                <div className="text-xs text-white/40">{order.customer_email}</div>
              </td>
              <td>{order.items?.length || 0} items</td>
              <td>£{order.total?.toFixed(2)}</td>
              <td>
                <span className={`status-badge ${order.status === 'In Queue' ? 'status-in-queue' : order.status === 'Printing' ? 'status-printing' : 'status-shipped'}`}>
                  {order.status}
                </span>
              </td>
              <td>
                <select 
                  value={order.status}
                  onChange={(e) => onStatusChange(order.id, e.target.value)}
                  className="bg-[#050505] border border-white/20 text-white px-3 py-2 text-sm"
                  data-testid={`order-status-select-${order.id}`}
                >
                  <option>In Queue</option>
                  <option>Printing</option>
                  <option>Shipped</option>
                  <option>Delivered</option>
                </select>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {activeOrders.length === 0 && <p className="text-center text-white/40 py-8">No active orders</p>}
    </div>
  );
};

// Admin Delivered Tab
const AdminDeliveredTab = ({ orders }) => {
  const deliveredOrders = useMemo(() => 
    orders.filter(order => order.status === 'Shipped' || order.status === 'Delivered'),
    [orders]
  );

  return (
    <div className="bg-[#111111] border border-white/10 overflow-x-auto">
      <table className="admin-table">
        <thead>
          <tr>
            <th>Order #</th>
            <th>Customer</th>
            <th>Items</th>
            <th>Total</th>
            <th>Status</th>
            <th>Date</th>
          </tr>
        </thead>
        <tbody>
          {deliveredOrders.map(order => (
            <tr key={order.id} data-testid={`delivered-order-${order.id}`}>
              <td className="mono">{order.order_number}</td>
              <td>
                <div>{order.customer_name}</div>
                <div className="text-xs text-white/40">{order.customer_email}</div>
              </td>
              <td>{order.items?.length || 0} items</td>
              <td className="text-[#00AE42]">£{order.total?.toFixed(2)}</td>
              <td><span className="status-badge status-shipped">{order.status}</span></td>
              <td className="text-white/40 text-sm">
                {order.created_at ? new Date(order.created_at).toLocaleDateString() : '-'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {deliveredOrders.length === 0 && <p className="text-center text-white/40 py-8">No delivered orders yet</p>}
    </div>
  );
};

// Admin Earnings Tab
const AdminEarningsTab = ({ orders }) => {
  const deliveredOrders = useMemo(() => 
    orders.filter(o => o.status === 'Shipped' || o.status === 'Delivered'),
    [orders]
  );

  const totalEarnings = useMemo(() => 
    deliveredOrders.reduce((sum, o) => sum + (o.total || 0), 0),
    [deliveredOrders]
  );

  const pendingRevenue = useMemo(() => 
    orders.filter(o => o.status !== 'Shipped' && o.status !== 'Delivered').reduce((sum, o) => sum + (o.total || 0), 0),
    [orders]
  );

  return (
    <div data-testid="earnings-tab">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="bg-[#111111] border border-white/10 p-6">
          <p className="text-white/60 text-sm uppercase tracking-wider mb-2">Total Earnings</p>
          <p className="text-3xl font-bold text-[#00AE42]">£{totalEarnings.toFixed(2)}</p>
          <p className="text-white/40 text-xs mt-2">From delivered orders</p>
        </div>
        <div className="bg-[#111111] border border-white/10 p-6">
          <p className="text-white/60 text-sm uppercase tracking-wider mb-2">Pending Revenue</p>
          <p className="text-3xl font-bold text-white">£{pendingRevenue.toFixed(2)}</p>
          <p className="text-white/40 text-xs mt-2">Orders in progress</p>
        </div>
        <div className="bg-[#111111] border border-white/10 p-6">
          <p className="text-white/60 text-sm uppercase tracking-wider mb-2">Total Orders</p>
          <p className="text-3xl font-bold text-white">{orders.length}</p>
          <p className="text-white/40 text-xs mt-2">{deliveredOrders.length} delivered</p>
        </div>
      </div>

      <div className="bg-[#111111] border border-white/10 p-6">
        <h3 className="font-bold mb-6">Earnings Breakdown</h3>
        <div className="space-y-4">
          {deliveredOrders.length === 0 ? (
            <p className="text-white/40 text-center py-8">No completed orders yet</p>
          ) : (
            deliveredOrders.map(order => (
              <div key={order.id} className="flex items-center justify-between py-3 border-b border-white/5">
                <div>
                  <span className="mono text-[#00AE42]">{order.order_number}</span>
                  <span className="text-white/40 ml-4">{order.customer_name}</span>
                </div>
                <div className="text-right">
                  <span className="font-bold">£{order.total?.toFixed(2)}</span>
                  <span className="text-white/40 text-sm ml-4">
                    {order.created_at ? new Date(order.created_at).toLocaleDateString() : ''}
                  </span>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

// Admin Custom Orders Tab
const AdminCustomOrdersTab = ({ customOrders, onStatusChange }) => {
  return (
    <div className="bg-[#111111] border border-white/10 overflow-x-auto">
      <table className="admin-table">
        <thead>
          <tr>
            <th>Order #</th>
            <th>Customer</th>
            <th>File</th>
            <th>Material</th>
            <th>Color</th>
            <th>Status</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {customOrders.map(order => (
            <tr key={order.id} data-testid={`admin-custom-order-${order.id}`}>
              <td className="mono">{order.order_number}</td>
              <td>
                <div>{order.customer_name}</div>
                <div className="text-xs text-white/40">{order.customer_email}</div>
              </td>
              <td className="text-xs">{order.file_name}</td>
              <td>{order.material}</td>
              <td>{order.color}</td>
              <td>
                <span className={`status-badge ${order.status === 'Quote Requested' ? 'status-in-queue' : 'status-printing'}`}>
                  {order.status}
                </span>
              </td>
              <td>
                <select 
                  value={order.status}
                  onChange={(e) => onStatusChange(order.id, e.target.value, true)}
                  className="bg-[#050505] border border-white/20 text-white px-3 py-2 text-sm"
                  data-testid={`custom-order-status-select-${order.id}`}
                >
                  <option>Quote Requested</option>
                  <option>Quote Sent</option>
                  <option>In Queue</option>
                  <option>Printing</option>
                  <option>Shipped</option>
                </select>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {customOrders.length === 0 && <p className="text-center text-white/40 py-8">No custom orders yet</p>}
    </div>
  );
};

// Admin Social Links Tab
const AdminSocialTab = ({ socialLinks, onRefresh }) => {
  const [showForm, setShowForm] = useState(false);
  const [socialForm, setSocialForm] = useState({ platform: '', url: '', icon: '' });

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/social-links`, socialForm);
      toast.success('Social link added');
      setShowForm(false);
      setSocialForm({ platform: '', url: '', icon: '' });
      onRefresh();
    } catch {
      toast.error('Failed to add social link');
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/admin/social-links/${id}`);
      toast.success('Social link deleted');
      onRefresh();
    } catch {
      toast.error('Failed to delete');
    }
  };

  return (
    <div>
      <button 
        onClick={() => setShowForm(true)}
        className="btn-solid px-6 py-3 font-bold text-sm uppercase tracking-wider mb-6 flex items-center gap-2"
        data-testid="add-social-btn"
      >
        <Plus size={20} /> Add Social Link
      </button>

      {showForm && (
        <div className="bg-[#111111] border border-white/10 p-6 mb-6" data-testid="social-form">
          <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <input 
              placeholder="Platform (e.g., Instagram)"
              value={socialForm.platform}
              onChange={(e) => setSocialForm({...socialForm, platform: e.target.value})}
              className="input-dark"
              required
              data-testid="social-platform-input"
            />
            <input 
              placeholder="URL"
              value={socialForm.url}
              onChange={(e) => setSocialForm({...socialForm, url: e.target.value})}
              className="input-dark"
              required
              data-testid="social-url-input"
            />
            <input 
              placeholder="Icon name"
              value={socialForm.icon}
              onChange={(e) => setSocialForm({...socialForm, icon: e.target.value})}
              className="input-dark"
              data-testid="social-icon-input"
            />
            <div className="flex gap-4 md:col-span-3">
              <button type="submit" className="btn-solid px-6 py-3 font-bold text-sm" data-testid="save-social-btn">Add Link</button>
              <button type="button" onClick={() => setShowForm(false)} className="btn-primary px-6 py-3 text-sm">Cancel</button>
            </div>
          </form>
        </div>
      )}

      <div className="space-y-2">
        {socialLinks.map(link => (
          <div key={link.id} className="bg-[#111111] border border-white/10 p-4 flex items-center justify-between" data-testid={`social-link-${link.id}`}>
            <div>
              <span className="font-bold">{link.platform}</span>
              <span className="text-white/40 ml-4">{link.url}</span>
            </div>
            <button onClick={() => handleDelete(link.id)} className="text-[#FF3B30] hover:underline text-sm" data-testid={`delete-social-${link.id}`}>Delete</button>
          </div>
        ))}
        {socialLinks.length === 0 && <p className="text-white/40 py-8 text-center">No social links yet</p>}
      </div>
    </div>
  );
};

// Admin Dashboard
const AdminDashboard = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [activeTab, setActiveTab] = useState('products');
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [customOrders, setCustomOrders] = useState([]);
  const [socialLinks, setSocialLinks] = useState([]);

  const fetchData = useCallback(async () => {
    try {
      const [prodRes, ordRes, custRes, socRes] = await Promise.all([
        axios.get(`${API}/products`),
        axios.get(`${API}/admin/orders`),
        axios.get(`${API}/admin/custom-orders`),
        axios.get(`${API}/social-links`)
      ]);
      setProducts(prodRes.data);
      setOrders(ordRes.data);
      setCustomOrders(custRes.data);
      setSocialLinks(socRes.data);
    } catch {
      // Silent fail
    }
  }, []);

  useEffect(() => {
    if (isLoggedIn) fetchData();
  }, [isLoggedIn, fetchData]);

  const handleStatusChange = useCallback(async (orderId, status, isCustom = false) => {
    try {
      const endpoint = isCustom 
        ? `${API}/admin/custom-orders/${orderId}/status`
        : `${API}/admin/orders/${orderId}/status`;
      await axios.put(endpoint, { status });
      toast.success('Status updated');
      fetchData();
    } catch {
      toast.error('Failed to update status');
    }
  }, [fetchData]);

  const tabs = useMemo(() => [
    { id: 'products', label: 'Products' },
    { id: 'orders', label: 'Orders' },
    { id: 'delivered', label: 'Delivered' },
    { id: 'earnings', label: 'Earnings' },
    { id: 'custom', label: 'Custom Orders' },
    { id: 'social', label: 'Social Links' }
  ], []);

  if (!isLoggedIn) {
    return <AdminLogin onLogin={() => setIsLoggedIn(true)} />;
  }

  return (
    <div className="min-h-screen" data-testid="admin-dashboard">
      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-2xl font-bold">ADMIN DASHBOARD</h1>
          <button onClick={() => setIsLoggedIn(false)} className="text-white/60 hover:text-white text-sm">Logout</button>
        </div>

        <div className="flex gap-4 mb-8 border-b border-white/10 overflow-x-auto">
          {tabs.map(tab => (
            <button 
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`pb-4 px-4 text-sm uppercase tracking-wider transition-colors whitespace-nowrap ${
                activeTab === tab.id 
                  ? 'text-[#00AE42] border-b-2 border-[#00AE42]' 
                  : 'text-white/60 hover:text-white'
              }`}
              data-testid={`admin-tab-${tab.id}`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {activeTab === 'products' && <AdminProductsTab products={products} onRefresh={fetchData} />}
        {activeTab === 'orders' && <AdminOrdersTab orders={orders} onStatusChange={handleStatusChange} />}
        {activeTab === 'delivered' && <AdminDeliveredTab orders={orders} />}
        {activeTab === 'earnings' && <AdminEarningsTab orders={orders} />}
        {activeTab === 'custom' && <AdminCustomOrdersTab customOrders={customOrders} onStatusChange={handleStatusChange} />}
        {activeTab === 'social' && <AdminSocialTab socialLinks={socialLinks} onRefresh={fetchData} />}
      </div>
    </div>
  );
};

function App() {
  return (
    <CartProvider>
      <div className="App min-h-screen bg-[#050505]">
        <Toaster position="top-right" theme="dark" />
        <BrowserRouter>
          <Routes>
            <Route path="/admin" element={<AdminDashboard />} />
            <Route path="*" element={
              <>
                <Header />
                <main>
                  <Routes>
                    <Route path="/" element={<Home />} />
                    <Route path="/cart" element={<CartPage />} />
                    <Route path="/checkout" element={<CheckoutPage />} />
                  </Routes>
                </main>
                <Footer />
              </>
            } />
          </Routes>
        </BrowserRouter>
      </div>
    </CartProvider>
  );
}

export default App;
